'use client';

import { type ReactNode } from 'react';
import { useLocation } from 'react-router';

import { useAppConfig } from '#/config/AppConfigContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { I18nLocale } from '#/i18n/types/I18nLocale';
import type { DatetimeString } from '#/types/TypedDateString';
import { getStaticImageSrc } from '#/utils/getImageSrc';
import { log } from '#/utils/logging';

type Props = {
  title: string;
  description?: string | null;
  children: ReactNode;
  ogImage?: string | { url: string; alt: string | null } | null;
  robotsNoIndex?: boolean;
  article?: {
    firstPublished: DatetimeString | null;
    lastModified: DatetimeString | null;
    author: string | null;
    tags: string[];
  } | null;
  linkRels?: {
    rssFeed?: { title: string; href: string } | null;
    translations?: Array<{ url: string; locale: I18nLocale }>;
    canonical: string;
  } | null;
  schemaMarkups?: (string | null | undefined)[];
};

export function RouteModule({
  title,
  description,
  children,
  ogImage,
  robotsNoIndex,
  article,
  linkRels,
  schemaMarkups,
}: Props) {
  const { appConfig } = useAppConfig();
  const { i18n } = useI18n();
  const location = useLocation();
  const isInStaffRoute =
    location.pathname.startsWith('/staff') ||
    location.pathname.startsWith('/cms');

  const affixedTitle = isInStaffRoute ? '🧑‍💼 ' + title : `${title} - Idealist`;

  const absoluteUrl = (value: string) => {
    if (!value) {
      log.error(`Invalid url for metadata on ${location.pathname}`);
      return value;
    }

    if (value.startsWith('http://') || value.startsWith('https://'))
      return value;
    return `${appConfig.idealist.appUrl}/${value.startsWith('/') ? value.slice(1) : value}`;
  };

  const ogImageUrl = typeof ogImage === 'string' ? ogImage : ogImage?.url;

  return (
    <>
      {(!appConfig.idealist.allowRobots || robotsNoIndex) && (
        <meta name="robots" content="noindex, nofollow" />
      )}

      <title>{affixedTitle}</title>
      <meta property="og:title" content={title} />

      <meta
        name="description"
        content={
          description ??
          i18n(
            'Idealist connects millions of idealists – people who want to do good – with opportunities for action and collaboration all over the world.',
          )
        }
      />

      <meta name="twitter:card" content="summary_large_image" />

      <meta
        property="og:image"
        content={absoluteUrl(
          ogImageUrl ??
            getStaticImageSrc('og-image.jpg', appConfig.idealist.staticImgPath),
        )}
      />
      {ogImage && 'alt' && typeof ogImage === 'object' && ogImage.alt && (
        <meta property="og:image:alt" content={ogImage.alt} />
      )}

      {article && (
        <>
          {article.firstPublished && (
            <meta
              property="article:published_time"
              content={article.firstPublished}
            />
          )}

          {article.lastModified && (
            <meta
              property="article:modified_time"
              content={article.lastModified}
            />
          )}

          {article.author && (
            <>
              <meta property="article:author" content={article.author} />
              <meta
                property="profile:first_name"
                content={article.author.split(' ')[0]}
              />
              <meta
                property="profile:last_name"
                content={article.author.split(' ').pop()}
              />
            </>
          )}

          {article.tags.map((tag) => (
            <meta key={tag} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {linkRels && (
        <>
          {linkRels.rssFeed && (
            <link
              rel="alternate"
              type="application/rss+xml"
              title={linkRels.rssFeed.title}
              href={absoluteUrl(linkRels.rssFeed.href)}
            />
          )}

          {linkRels.translations && (
            <>
              {linkRels.translations.map((translation) => (
                <link
                  key={translation.locale}
                  rel="alternate"
                  href={absoluteUrl(translation.url)}
                  hrefLang={translation.locale}
                />
              ))}

              <link
                rel="alternate"
                href={absoluteUrl(linkRels.canonical)}
                hrefLang="x-default"
              />
            </>
          )}

          <link rel="canonical" href={absoluteUrl(linkRels.canonical)} />
        </>
      )}

      {schemaMarkups?.map(
        (markup, index) =>
          markup && (
            <script
              // oxlint-disable-next-line no-array-index-key
              key={index}
              type="application/ld+json"
            >
              {markup}
            </script>
          ),
      )}

      {children}
    </>
  );
}
